/**
 * How far the translated *voice* is behind the translated *text*.
 *
 * Every latency this app measured until now was about text: first input word
 * to first output word, last input delta against last output delta. None of it
 * says when a listener actually heard anything, and the two are not the same
 * question.
 *
 * They cannot be. The provider writes the translation as fast as the model
 * produces it and speaks it in real time — a five-second sentence takes five
 * seconds to say however quickly it was generated — so the text necessarily
 * runs ahead of the voice. That much is arithmetic, not a fault.
 *
 * What is worth knowing is whether the gap **grows**. If speech is produced
 * faster than it can be played while someone keeps talking, the audio backs up
 * and the listener hears the translation further and further behind. A
 * constant lead is a property of the medium; a growing one is a problem, and
 * the difference is invisible in every number collected so far.
 *
 * Three signals, in order of how directly they answer it:
 *
 *   - `acceleratedMs` — audio the jitter buffer *discarded to catch up*.
 *     Rising means the player is compressing speech because it is behind.
 *     This is the least ambiguous evidence there is.
 *   - `playoutDelayMs` — how long the average sample waited before it was
 *     played. Rising means a queue is forming.
 *   - `audioPlayedMs` — seconds of translated audio actually played, which is
 *     also what settled the question below.
 *
 * There was a third number here, `voiceBehindTextMs`: text produced minus
 * audio played. It was removed after two staging calls, and the reason is
 * worth keeping so nobody adds it back. `audioPlayedMs` came out equal to the
 * session duration almost second for second — 174 180 of 176 000, then 99 120
 * of 101 000 — so the provider sends **continuous** audio, silence included.
 * Played time therefore tracks the wall clock, and the difference measured how
 * long nobody was speaking rather than how far the voice had fallen behind.
 * It reported minus forty-nine seconds on a call where nothing was wrong.
 *
 * Measuring it properly needs something that tells speech from silence in the
 * received audio — `totalAudioEnergy` is the obvious candidate and was not
 * pursued, because the two numbers above already answered the question that
 * prompted all this: the buffer holds tens of milliseconds and discards
 * fractions of a second over minutes of talking. The voice is not falling
 * behind and the gap is not growing.
 */

/** The fields of one `inbound-rtp` audio report this needs. All optional. */
export interface PlayoutSample {
  /** Cumulative seconds all samples spent waiting in the jitter buffer. */
  jitterBufferDelay?: number;
  jitterBufferEmittedCount?: number;
  /** Cumulative seconds of audio handed to the output device. */
  totalSamplesDuration?: number;
  totalSamplesReceived?: number;
  /** Samples dropped to speed playback up, and invented to slow it down. */
  removedSamplesForAcceleration?: number;
  insertedSamplesForDeceleration?: number;
}

export interface PlayoutMetrics {
  /** Mean milliseconds a sample waited before being played. */
  playoutDelayMs: number | null;
  /** Milliseconds of translated audio actually played. */
  audioPlayedMs: number | null;
  /** Milliseconds of it thrown away so playback could catch up. */
  acceleratedMs: number | null;
  /** Milliseconds invented because there was not enough audio to play. */
  stretchedMs: number | null;
}

/** WebRTC audio is 48 kHz in practice; the real rate is derived when it can be. */
const ASSUMED_SAMPLE_RATE = 48_000;

export class PlayoutTracker {
  #latest: PlayoutSample | null = null;

  /** One `inbound-rtp` audio report, as `getStats` gave it. */
  observeStats(sample: PlayoutSample): void {
    this.#latest = sample;
  }

  get metrics(): PlayoutMetrics {
    const s = this.#latest;
    if (!s) {
      return {
        playoutDelayMs: null,
        audioPlayedMs: null,
        acceleratedMs: null,
        stretchedMs: null,
      };
    }

    const rate =
      s.totalSamplesReceived !== undefined &&
      s.totalSamplesDuration !== undefined &&
      s.totalSamplesDuration > 0
        ? s.totalSamplesReceived / s.totalSamplesDuration
        : ASSUMED_SAMPLE_RATE;

    const emitted = s.jitterBufferEmittedCount ?? 0;
    const playoutDelayMs =
      s.jitterBufferDelay !== undefined && emitted > 0
        ? Math.round((s.jitterBufferDelay / emitted) * 1000)
        : null;

    const audioPlayedMs =
      s.totalSamplesDuration !== undefined ? Math.round(s.totalSamplesDuration * 1000) : null;

    const samplesToMs = (n: number | undefined) =>
      n === undefined ? null : Math.round((n / rate) * 1000);

    return {
      playoutDelayMs,
      audioPlayedMs,
      acceleratedMs: samplesToMs(s.removedSamplesForAcceleration),
      stretchedMs: samplesToMs(s.insertedSamplesForDeceleration),
    };
  }
}
